<script lang="ts">
    import Icon from '../../components/Icon.svelte';
    import { useNavigate } from 'svelte-navigator';
    import ButtonRounded from '../../common/ButtonRounded.svelte';
    import { sendFeedback } from '../../domain/session/receivers.actions';
    import type { Rate } from '../../types/user';
    import authStore from '../../store/auth';

    const navigate = useNavigate();
    
    let review: Rate = {
        category: null,
        name: null,
        email: null,
        message: null,
        rating: null,
        mobile: null
    }

    let event: string = ""
    let errors: Record<string, string> = {}
    let stages: string[] = [
        "start",
        "review",
        "thank-you"
    ]
    let stage: number = $authStore.user.email ? 1 : 0;

    const isValidEmail = email => {
        const emailRegex = new RegExp(/^[A-Za-z0-9_!#$%&'*+\/=?`{|}~^.-]+@[A-Za-z0-9.-]+$/, "gm");
        return emailRegex.test(email);
    }

    const processEmail = () => {
        if (!review.email || (review.email && !isValidEmail(review.email))) {
            errors.email = "Email is required";
            return
        }

        authStore.setEmail(review.email);

        stage = 1;
    }

    const processReview = async () => {
        if (!review.message ) {
            errors.message = "Review is required";
            return
        }

        const user = $authStore.user
        const data = {
            ...review,
            ...user
        }
        
        authStore.setUser(data);
        event = "loading";
        
        try {
            const payload: Rate = {
                ...review,
                name: `${$authStore.user?.first_name} ${$authStore.user?.last_name}`,
                email: review.email,
                mobile: user.mobile
            }
            await sendFeedback(payload)
            stage = 2;
        } catch (e) {} finally {
            event = "";
        }
    }
    
</script>

<div class="flex h-full justify-center items-center">
    <div class="w-full">
        <div class="display-flex">
            <div class="flex flex-col">
                {#if stages[stage] === stages[0]}
                <div class="relative mx-8 my-16 secondary-text-color">
                    <strong class="primary-text-color text-lg">
                        Thank you for contacting us! 
                    </strong>
                    Please provide your email address below to receive a summary of the call.
                    <div class="mt-4">
                        <span class="text-xs">
                            Enter your email address
                        </span>
                        <div class="flex">
                            <input
                                placeholder="Enter your email"
                                bind:value={review.email}
                                required
                                class="border-0 border-b border-black mr-2 px-2 py-1 text-sm primary-text-color"
                            />
                            <ButtonRounded
                                disabled={false}
                                on:click={processEmail}
                                size=8
                                title=""
                                icon="right-arrow"
                                iconColor="#30A059"
                                iconSize=16
                                className="call-btn-bg-color w-10 h-8"
                            />
                        </div>
                    </div>
                    {#if errors.email}
                        <small class="error">{ errors.email }</small>
                    {/if}
                </div>
                {/if}
                {#if stages[stage] === stages[1]}
                <div class="relative mx-8 mt-12 mb-4 secondary-text-color text-xs">
                    <strong class="primary-text-color text-lg">
                        We're sorry, we're unable to take your call at the moment.
                    </strong>
                    Please leave a message, and we'll get back to you as soon as possible.
                    <div class="mt-4">
                        <div class="">
                            <span class="text-xs">
                                Select Categories
                            </span>
                            <select
                                bind:value={review.category}
                                class="border-0 border-b border-black px-2 py-1 text-sm w-full primary-text-color"
                            >
                                <option value="Sales Enquiries">Sales Enquiries</option>
                                <option value="Product/Service Issue">Product/Service Issue</option>
                                <option value="Payment Issues">Payment Issues</option>
                                <option value="Report or Feedback">Report or Feedback</option>
                            </select>
                        </div>
                        <div class="mt-4">
                            <!-- 
                            <span class="text-xs">
                                Enter your message
                            </span> 
                            -->
                            <div class="flex">
                                <input
                                    placeholder="Enter your message"
                                    bind:value={review.message}
                                    required
                                    class="border-0 border-b border-black w-full px-2 py-1 text-sm primary-text-color"
                                />
                            </div>
                        </div>
                        <ButtonRounded
                            disabled={false}
                            on:click={processReview}
                            size=8
                            title=""
                            className="call-btn-bg-color h-10 mt-4 flex-inline text-white w-full"
                        >
                        Submit
                        </ButtonRounded>
                        {#if errors.message}
                            <small class="error">{ errors.message }</small>
                        {/if}
                    </div>
                </div>
                {/if}
                {#if stages[stage] === stages[2]}
                <div class="relative flex flex-col justify-center items-center mx-8 my-16 secondary-text-color text-center">
                    <img src="images/icons/thanks.png" width="50px" alt="thanks">
                    <strong>
                        Thank you for leaving your message!
                    </strong>
                    <br/>
                    <span class="text-xs">
                        We've received your message and will get back to you shortly.
                    </span>
                    <ButtonRounded
                        disabled={false}
                        on:click={() => navigate("/")}
                        size=8
                        title=""
                        className="call-btn-bg-color h-10 mt-4 flex-inline text-white px-8"
                    >
                        Return home
                    </ButtonRounded>
                </div>
                {/if}
                {#if event !== ""}
                <div class="flex mx-1">
                    <span class={`${event === "loading" ? "animate-spin" : ""} mx-2`}>
                        <Icon icon={event} size={24} color="#30A059" />
                    </span>
                    <span class="mx-2">
                        {#if event === "loading"}
                            Connecting.
                        {:else}
                            Ringing.
                        {/if}
                    </span>
                </div>
                {/if}
            </div>
        </div>
    </div>
</div>

<style lang="scss">
    .primary-text-color {
        color: var(--primary-text-color)
    }
    .secondary-text-color {
        color: var(--secondary-text-color)
    }
    .primary-bg-text-color {
        color: var(--primary-bg-color)
    }
    .secondary-bg-text-color {
        color: var(--secondary-bg-color)
    }

    .error {
        color: #f00;
    }
</style>